import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from './api';

const PortfolioContext = createContext(null);

export function PortfolioProvider({ children }) {
  const [portfolios, setPortfolios] = useState([]);
  const [activeId, setActiveId] = useState(null); // null = all, or portfolio_id
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPortfolios = useCallback(async () => {
    try {
      const { data } = await api.get('/portfolios');
      setPortfolios(data);
      if (data.length > 0 && activeId === null) {
        setActiveId('all');
      }
    } catch { /* ignore */ }
  }, []);

  const loadAssets = useCallback(async () => {
    setLoading(true);
    try {
      if (activeId === 'all' || activeId === null) {
        // Load all assets across portfolios
        let all = [];
        for (const pf of portfolios) {
          const { data } = await api.get(`/portfolios/${pf.portfolio_id}`);
          all = [...all, ...(data.assets || [])];
        }
        setAssets(all);
      } else {
        const { data } = await api.get(`/portfolios/${activeId}`);
        setAssets(data.assets || []);
      }
    } catch { setAssets([]); }
    finally { setLoading(false); }
  }, [activeId, portfolios]);

  useEffect(() => { loadPortfolios(); }, [loadPortfolios]);
  useEffect(() => { if (portfolios.length > 0) loadAssets(); }, [portfolios, activeId, loadAssets]);

  const switchPortfolio = (id) => setActiveId(id);
  const activeName = activeId === 'all' ? 'All Portfolios' : portfolios.find(p => p.portfolio_id === activeId)?.name || 'Portfolio';
  const refresh = () => { loadPortfolios(); loadAssets(); };

  return (
    <PortfolioContext.Provider value={{ portfolios, activeId, activeName, assets, loading, switchPortfolio, refresh }}>
      {children}
    </PortfolioContext.Provider>
  );
}

export function usePortfolio() {
  return useContext(PortfolioContext);
}
